/**
 * CustomersBrowsePage — `/admin/customers`.
 *
 * Staff console "Customers" admin browse surface. Lists every customer in the
 * tenant via `GET /v1/customers/admin` (Admin/listCustomers). Filter state
 * lives in URL search params so deep links + back/forward stay coherent.
 *
 * Backend contract: see `lib/api/customers.ts::listAdminCustomers` and the
 * service-customer DTO at `customer.dto.ts::ListAdminCustomersResponseDto`.
 *
 * If the endpoint is not yet deployed (404 / network error) the page falls
 * back to a `DeferredNotice` so the IA is still walkable.
 */
import { type JSX, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, Users } from 'lucide-react';

import { ApiError } from '../../../lib/api/client';
import {
  listAdminCustomers,
  type AdminCustomerSummary,
  type ListAdminCustomersQuery,
} from '../../../lib/api/customers';
import { DeferredNotice, PageHeader } from '../../shared/StaffUI';

const DEFAULT_PAGE_SIZE = 25;
const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'fullName:asc', label: 'Name A → Z' },
  { value: 'fullName:desc', label: 'Name Z → A' },
] as const;

function fromSearchParams(sp: URLSearchParams): ListAdminCustomersQuery {
  const geoStateRaw = sp.get('geoStateId');
  const pageRaw = sp.get('page');
  const sizeRaw = sp.get('pageSize');
  return {
    q: sp.get('q') ?? undefined,
    grade: sp.get('grade') ?? undefined,
    geoStateId:
      geoStateRaw && Number.isFinite(Number(geoStateRaw))
        ? Number(geoStateRaw)
        : undefined,
    createdFrom: sp.get('createdFrom') ?? undefined,
    createdTo: sp.get('createdTo') ?? undefined,
    page: pageRaw && Number.isFinite(Number(pageRaw)) ? Number(pageRaw) : 1,
    pageSize:
      sizeRaw && Number.isFinite(Number(sizeRaw)) ? Number(sizeRaw) : DEFAULT_PAGE_SIZE,
    sort: sp.get('sort') ?? 'createdAt:desc',
  };
}

function setParam(
  sp: URLSearchParams,
  key: string,
  value: string | number | undefined,
): URLSearchParams {
  const next = new URLSearchParams(sp);
  if (value === undefined || value === '' || value === null) {
    next.delete(key);
  } else {
    next.set(key, String(value));
  }
  // Reset to page 1 whenever filters/sort change.
  if (key !== 'page') next.delete('page');
  return next;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
}

function gradeBadge(grade: string): JSX.Element {
  const tone =
    grade === 'REGISTERED'
      ? 'bg-nipost-green-light text-nipost-green'
      : 'bg-gray-100 text-gray-600';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${tone}`}
    >
      {grade.replace(/_/g, ' ')}
    </span>
  );
}

interface SortIconProps {
  readonly active: boolean;
  readonly direction: 'asc' | 'desc';
}

function SortIcon({ active, direction }: SortIconProps): JSX.Element {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-50" />;
  return direction === 'asc' ? (
    <ArrowUp className="w-3 h-3" />
  ) : (
    <ArrowDown className="w-3 h-3" />
  );
}

export function CustomersBrowsePage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const query = useMemo(() => fromSearchParams(sp), [sp]);

  const result = useQuery({
    queryKey: ['admin', 'customers', 'list', query] as const,
    queryFn: () => listAdminCustomers(query),
    retry: false,
    staleTime: 30_000,
  });

  function update(key: string, value: string | number | undefined): void {
    setSp(setParam(sp, key, value));
  }

  function toggleSort(field: 'createdAt' | 'fullName'): void {
    const current = query.sort ?? 'createdAt:desc';
    const [activeField, activeDir] = current.split(':') as [string, string];
    const nextDir =
      activeField === field ? (activeDir === 'asc' ? 'desc' : 'asc') : field === 'fullName' ? 'asc' : 'desc';
    update('sort', `${field}:${nextDir}`);
  }

  const items: readonly AdminCustomerSummary[] = result.data?.items ?? [];
  const total = result.data?.total ?? 0;
  const page = result.data?.page ?? query.page ?? 1;
  const pageSize = result.data?.pageSize ?? query.pageSize ?? DEFAULT_PAGE_SIZE;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const isMissing =
    result.isError &&
    result.error instanceof ApiError &&
    (result.error.status === 404 || result.error.status === 501);

  const isDenied =
    result.isError &&
    result.error instanceof ApiError &&
    (result.error.status === 401 || result.error.status === 403);

  const [sortField, sortDir] = (query.sort ?? 'createdAt:desc').split(':') as [
    'createdAt' | 'fullName',
    'asc' | 'desc',
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        description="Browse every customer captured across the tenant. Click a row to open the customer 360."
        actions={
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted" />
            <Link
              to="/admin/customers/dashboard"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium"
            >
              Reports & dashboard
            </Link>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="bg-white border border-border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label htmlFor="cust-q" className="text-[10px] font-semibold uppercase text-muted">
              Search
            </label>
            <input
              id="cust-q"
              type="search"
              placeholder="Name, phone, email, CUS-…"
              defaultValue={query.q ?? ''}
              onChange={(e) => update('q', e.target.value.trim() || undefined)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              aria-label="Search customers"
              data-testid="cust-q-input"
            />
          </div>
          <div>
            <label htmlFor="cust-grade" className="text-[10px] font-semibold uppercase text-muted">
              Grade
            </label>
            <select
              id="cust-grade"
              value={query.grade ?? ''}
              onChange={(e) => update('grade', e.target.value || undefined)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-white"
            >
              <option value="">All grades</option>
              <option value="WALK_IN">Walk-in</option>
              <option value="REGISTERED">Registered</option>
            </select>
          </div>
          <div>
            <label htmlFor="cust-state" className="text-[10px] font-semibold uppercase text-muted">
              State (geoStateId)
            </label>
            <input
              id="cust-state"
              type="number"
              min={1}
              placeholder="e.g. 25"
              defaultValue={query.geoStateId ?? ''}
              onChange={(e) =>
                update(
                  'geoStateId',
                  e.target.value === '' ? undefined : Number(e.target.value),
                )
              }
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="cust-from" className="text-[10px] font-semibold uppercase text-muted">
              Registered from
            </label>
            <input
              id="cust-from"
              type="date"
              defaultValue={query.createdFrom ?? ''}
              onChange={(e) => update('createdFrom', e.target.value || undefined)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="cust-to" className="text-[10px] font-semibold uppercase text-muted">
              Registered to
            </label>
            <input
              id="cust-to"
              type="date"
              defaultValue={query.createdTo ?? ''}
              onChange={(e) => update('createdTo', e.target.value || undefined)}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      {result.isPending ? (
        <p className="text-sm text-muted" data-testid="cust-loading">Loading customers…</p>
      ) : isMissing ? (
        <DeferredNotice title="Endpoint not yet deployed">
          <p>
            <code className="font-mono">GET /v1/customers/admin</code> is not yet
            available on this environment. The contract is locked — check back
            once <code className="font-mono">service-customer</code> ships the
            admin list surface.
          </p>
        </DeferredNotice>
      ) : isDenied ? (
        <DeferredNotice title="Permission required">
          <p>
            The token does not carry{' '}
            <code className="font-mono">Admin/listCustomers</code>.
          </p>
        </DeferredNotice>
      ) : result.isError ? (
        <div
          className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900"
          data-testid="cust-error"
        >
          <p className="font-semibold">Customer service unreachable</p>
          <p>{(result.error as Error).message}</p>
        </div>
      ) : items.length === 0 ? (
        <p
          className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center"
          data-testid="cust-empty"
        >
          No customers match the current filters.
        </p>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl overflow-x-auto">
            <table className="w-full text-sm" data-testid="cust-table">
              <thead className="bg-gray-50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Customer #</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">
                    <button
                      type="button"
                      onClick={() => toggleSort('fullName')}
                      className="inline-flex items-center gap-1 uppercase"
                      data-testid="cust-sort-name"
                    >
                      Name
                      <SortIcon active={sortField === 'fullName'} direction={sortDir} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Phone</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Email</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Grade</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">City / State</th>
                  <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">
                    <button
                      type="button"
                      onClick={() => toggleSort('createdAt')}
                      className="inline-flex items-center gap-1 uppercase"
                      data-testid="cust-sort-created"
                    >
                      Registered
                      <SortIcon active={sortField === 'createdAt'} direction={sortDir} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    data-testid={`cust-row-${c.id}`}
                  >
                    <td className="px-4 py-2 text-xs font-mono">
                      <Link to={`/admin/customers/${c.id}`} className="text-nipost-green hover:underline">
                        {c.customerNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-900">
                      <Link to={`/admin/customers/${c.id}`} className="hover:underline">
                        {c.fullName}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-xs">{c.phone ?? '—'}</td>
                    <td className="px-4 py-2 text-xs">{c.email ?? '—'}</td>
                    <td className="px-4 py-2">{gradeBadge(c.grade)}</td>
                    <td className="px-4 py-2 text-xs">
                      {c.primaryAddress
                        ? `${c.primaryAddress.city ?? '—'}${
                            c.primaryAddress.state ? `, ${c.primaryAddress.state}` : ''
                          }`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">{formatDate(c.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-xs text-muted">
            <p>
              {total} {total === 1 ? 'customer' : 'customers'} · page {page} of {lastPage}
            </p>
            <div className="flex items-center gap-2">
              <select
                aria-label="Sort"
                value={query.sort ?? 'createdAt:desc'}
                onChange={(e) => update('sort', e.target.value)}
                className="px-2 py-1 border border-border rounded bg-white text-xs"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => update('page', Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1 border border-border rounded bg-white disabled:opacity-50"
                data-testid="cust-prev"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => update('page', Math.min(lastPage, page + 1))}
                disabled={page >= lastPage}
                className="px-3 py-1 border border-border rounded bg-white disabled:opacity-50"
                data-testid="cust-next"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
